import { EventEmitter as Emitter } from "eventemitter3";

import type { ObjectType, EmptyObjectType, ObjectKeys, FunctionType } from "models-webgl2";
import { makeAutoBindFunctions } from "utils/helpers/make-auto-bind-functions";

type EmitterCallback = FunctionType<any[], void>;

type EventEmitterType = ObjectType<EmitterCallback, string>;

type onListenersCallback<T extends EventEmitterType> = (emitter: EventEmitter<T>) => void;

export class EventEmitter<T extends EventEmitterType = EmptyObjectType> {
  private readonly _emitter = new Emitter();

  private readonly _listenersEmitter = new Emitter();
  private _prevListenersCount = 0;

  private _isBlocked = 0;
  public get isBlocked() {
    return !!this._isBlocked;
  }

  private _map: ObjectType<Set<EmitterCallback>, ObjectKeys<T>> = {} as any;

  public get events() {
    return {
      on: this.on,
      off: this.off,
      offEvent: this.offEvent,
      offAll: this.offAll,
      block: this.block,
      unblock: this.unblock,
      isBlocked: this.isBlocked,
    };
  }

  constructor() {
    makeAutoBindFunctions(this);
  }

  public block() {
    ++this._isBlocked;
  }

  public unblock() {
    this._isBlocked = Math.max(this._isBlocked - 1, 0);
  }

  public on<E extends ObjectKeys<T>>(
    event: E,
    callback: FunctionType<Parameters<T[E]>, void>,
  ): VoidFunction {
    this._map[event] = this._map[event] ?? new Set();
    this._map[event].add(callback as any);
    this._emitter.on(event.toString(), callback as any);
    this._emitListeners();

    return () => {
      this.off(event, callback);
    };
  }

  public off<E extends ObjectKeys<T>>(event: E, callback: FunctionType<Parameters<T[E]>, void>) {
    const set = this._map[event];

    if (!set) {
      throw new Error("You're trying to unsubscribe from event which not yet been subscribed");
    }

    const prevSize = set.size;
    set.delete(callback as any);
    const currSize = set.size;
    if (prevSize === currSize) {
      throw new Error("Callback passed to unsubscribe method has invalid reference");
    } else if (!currSize) {
      delete this._map[event];
    }

    this._emitter.removeListener(event.toString(), callback as any);
    this._emitListeners();
  }

  public offEvent<E extends ObjectKeys<T>>(event: E) {
    this._emitter.removeListener(event.toString());
    delete this._map[event];
    this._emitListeners();
  }

  public offAll() {
    this._emitter.removeAllListeners();
    this._map = {} as any;
    this._emitListeners();
  }

  public emit<E extends ObjectKeys<T>>(event: E, ...args: Parameters<T[E]>) {
    if (this.isBlocked) return;
    this._emitter.emit(event.toString(), ...args);
  }

  // onListener means that you can pass callback which will be fired each time
  // you subscribe | unsubscribe you regular callbacks, e.g. if something changed with regular emitter.
  public onListener(callback: onListenersCallback<T>) {
    this._listenersEmitter.on("listenersCountChange", callback);
  }

  public offListener(callback: onListenersCallback<T>) {
    this._listenersEmitter.removeListener("listenersCountChange", callback);
  }

  public offListeners() {
    this._listenersEmitter.removeAllListeners();
  }

  private _emitListeners() {
    const length = Object.values(this._map).reduce((acc, curr) => acc + curr.size, 0);
    if (this._prevListenersCount === length) return;
    this._prevListenersCount = length;
    this._listenersEmitter.emit("listenersCountChange", length);
  }
}

export default EventEmitter;
